import { differenceInDays } from 'date-fns';
import { InferGetServerSidePropsType, NextPage } from 'next';
import { NextSeo } from 'next-seo';

import { buildItemUrlAbsolute } from '@/components/Shortnews/Shortnews.utils';
import { ContentPageShortnewsDetail } from '@/components/Shortnews/ShortnewsDetail';
import {
	ShortnewsDetailsDocument,
	ShortnewsDetailsQuery,
	ShortnewsDetailsQueryVariables,
} from '@/components/Shortnews/__generated__/ShortnewsDetail.generated';
import { NAVIGATION_ITEM_SHORTNEWS } from '@/config/constants';
import { useEnvironment } from '@/cutils/context/EnvironmentContext';
import { notNullOrUndefined } from '@/cutils/misc/notNullOrUndefined';
import { CacheTag } from '@/cutils/page-context/edge-cache-tags';
import { createDefaultGetServerSideProps } from '@/cutils/page-context/server-side-props';
import buildContentPageSeoConfig from '@/cutils/seo/buildContentPageSeoConfig';
import buildContentPageStructuredData from '@/cutils/seo/schema-org/buildContentPageStructuredData';
import { throwOnGraphQLSemanticErrorForPaths } from '@/sutils/graphql-error-matcher';
import { extractSlugAndIdFromPathSegment } from '@/utils/pathUtils';
import formatTimestampShortnews from '@/utils/time/formatTimestampShortnews';
import { PageView } from 'react-tracktor';

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

const Page: NextPage<Props> = ({ article, shortnewsList, noIndex }) => {
	const { constants } = useEnvironment();
	const SEO_DESCRIPTION = article.text?.slice(0, 130) + ' ... Mehr bei BR24 ';
	const seoConfig = buildContentPageSeoConfig({
		description: SEO_DESCRIPTION,
		title: article.title,
		url: buildItemUrlAbsolute({ omId: article.omId, slug: article.slug }, constants.appUrl),
	});

	const structuredData = buildContentPageStructuredData(article, constants.appUrl);

	return (
		<>
			{/* Injects dynamic meta tags into the `<head />`. */}
			<NextSeo noindex={noIndex} {...seoConfig} />

			{/* Injects script tags with structured data. */}
			{structuredData}

			<PageView
				key={article.shareUrl || 'shortnews-archive'}
				pageViewData={{
					data: article.trackingInfos,
					event: { page: article.shareUrl, type: 'PageViewEvent' },
				}}
			/>

			<ContentPageShortnewsDetail
				byEditorialTeam={article.sourceOrigin}
				id={article.id}
				omId={article.omId}
				path={article.path}
				shareUrl={article.shareUrl}
				text={article.text}
				title={article.title}
				updateDate={article.formattedDate}
				shortNewsList={shortnewsList}
			/>
		</>
	);
};

const SHORTNEWS_PATH = ['mainNewsItem'];

export const getServerSideProps = createDefaultGetServerSideProps(async (ctx, apolloClient, withCacheTags) => {
	const navigationTitle = NAVIGATION_ITEM_SHORTNEWS.pageTitle;
	const { id: shortnewsOmId, slug: shortnewsSlug } = extractSlugAndIdFromPathSegment(ctx.params?.shortnews);

	if (!shortnewsOmId) {
		return { notFound: true };
	}

	try {
		const { data, errors } = await apolloClient.query<ShortnewsDetailsQuery, ShortnewsDetailsQueryVariables>({
			query: ShortnewsDetailsDocument,
			variables: { shortnewsOmId },
		});

		throwOnGraphQLSemanticErrorForPaths([SHORTNEWS_PATH], errors);

		if (!data.mainNewsItem) {
			ctx.req.logger.warn(`Shortnews with id '${shortnewsOmId}' not found`);
			return { notFound: true };
		}

		const shortnewsArticle = data.mainNewsItem;

		if (shortnewsArticle.path !== `/${shortnewsSlug},${shortnewsOmId}`) {
			const { search } = new URL(ctx.resolvedUrl, 'http://localhost');

			return {
				redirect: {
					destination: `/meldung${shortnewsArticle.path}${search}`,
					permanent: true,
				},
			};
		}

		if (!(shortnewsArticle.updateDate && shortnewsArticle.sourceOrigin)) {
			return { notFound: true };
		}

		const formattedDate = formatTimestampShortnews(shortnewsArticle.updateDate);

		const article = {
			title: shortnewsArticle.title,
			text: shortnewsArticle.text,
			formattedDate,
			sourceOrigin: shortnewsArticle.sourceOrigin,
			slug: shortnewsArticle.slug,
			omId: shortnewsArticle.omId,
			id: shortnewsArticle.id,
			path: shortnewsArticle.path,
			shareUrl: shortnewsArticle.shareUrl,
			trackingInfos: shortnewsArticle.trackingInfos ?? {},
		};

		// add no-index if older than 7 days
		const currentDate = new Date();
		const updateDate = new Date(shortnewsArticle.updateDate);
		const result = differenceInDays(currentDate, updateDate);
		const noIndex: boolean = result >= 7;

		const shortnewsList = (data.openmedia?.mainList?.edges?.[0]?.node?.items?.edges?.map((edge) => edge?.node) ?? [])
			.filter(notNullOrUndefined)
			.filter((article) => !!article.title);

		const cacheTags = new Set<CacheTag>();
		if (withCacheTags) {
			cacheTags.add(`sn.${shortnewsArticle.omId}`);

			for (const { omId } of shortnewsList) {
				cacheTags.add(`sn.${omId}`);
			}
		}

		return {
			props: {
				article,
				shortnewsList,
				navigationTitle,
				noIndex,
				cacheTags,
			},
		};
	} catch (error: any) {
		ctx.req.logger.error(`Error during shortnews resolution | ${error.message}`, error);
		throw error;
	}
});

export default Page;
